import { client } from "@/client";
import { LoginUserResponse, Role } from "@/graphql/__generated__/graphql";
import { useUserData } from "@/hooks/cache/useUserData";
import { useAiImportStore } from "@/store/aiImports";
import { useDocumentChatStore } from "@/store/documentChat";
import { usePaginationStore } from "@/store/paginationStore";
import useUserStore from "@/store/shield";
import { jwtDecode } from "jwt-decode";

/**
 * This function checks if the User is logged in by validating the Users Token
 * @params usertypes: [String]: The types that the user has to have at least one of
 * @returns true if there is a valid Token in local Storage, false otherwise
 */
export function ValidateToken(roles: string[]) {
  const user = useUserData();
  // If the user is not present, log out
  if(!user) {
    return false;
  }

  const userHasRole = roles.includes(user.role) || roles.includes("ALL");
  return userHasRole;
}

// Checks if the user object exists
export function userExists() {
  const user = useUserData();
  return user ? true : false;
}

/**
 * Helper function to determine whether or not a user is a member of the main organization
 * Decodes the logged in user's token and compares organizationId to mainOrganizationId
 * @returns true if the user is a member of the main organization, false otherwise
 */
export const isMemberOfMainOrganization = (): boolean => {
  const currentToken = sessionStorage.getItem("token");

  if(!currentToken) return false;

  const { payload: { mainOrganizationId, organizationId }}: DecodedToken  = jwtDecode(currentToken!);

  return organizationId === mainOrganizationId;
};

/**
 * Extracts the main organization ID from the user's token
 * @returns  {number | undefined} The main organization ID
 */
export const getMainOrganizationId = (): number | undefined => {
  const currentToken = sessionStorage.getItem("token");

  if(!currentToken) return undefined;

  const { payload: { mainOrganizationId }}: DecodedToken  = jwtDecode(currentToken!);

  return mainOrganizationId;
};

/**
 * Helper function to determine whether or not the current user is a Manager
 * @returns {boolean} true if the user is a Manager, false otherwise
 */
export const isManager = (): boolean => {
  const { userRole } = useUserStore.getState();
  return (userRole === Role.Admin || userRole === Role.Manager);
};

/**
 * Helper function to determine whether or not the current user is an Admin
 * @returns {boolean} true if the user is an Admin, false otherwise
 */
export const isAdmin = (): boolean => {
  const { userRole } = useUserStore.getState();
  return userRole === Role.Admin;
};

/**
 * Helper function to determine whether or not the current user is an Editor
 * @returns {boolean} true if the user is an Editor, false otherwise
 */
export const isEditor = (): boolean => {
  const { userRole } = useUserStore.getState();
  return userRole === Role.Editor;
};

/**
 * Helper function to determine whether or not the current user is a Viewer
 * @returns {boolean} true if the user is a Viewer, false otherwise
 */
export const isViewer = (): boolean => {
  const { userRole } = useUserStore.getState();
  return userRole === Role.Viewer;
};

/**
 * Function to set the session storage and initialize the cache on login
 * @param {LoginUserResponse} result
 */
export async function login (result: LoginUserResponse) {
  // Set session Storage
  sessionStorage.setItem("token", result.token);
  sessionStorage.setItem("loggedIn", Date.now().toString());
  // Set default language to German
  sessionStorage.setItem("i18nextLng", "de");

  // Get userStore
  const userStore = useUserStore.getState();

  // Reset the store to ensure no previous state is carried over
  userStore.reset();

  // Update the 'shield' store with a single setUser call
  userStore.setUser({
    orgName: result.user.organization?.name || "",
    orgId: result.user.organization?.id || 0,
    userId: result.user.id,
    userRole: result.user.role,
    isConfigAdmin: result.user.isConfigAdmin,
  });
}

/**
 * Function to clear the session storage and cache on logout
 */
export async function logout() {
  const keysToKeep = ["darkMode", "i18nextLng", "ai-imports-store"];
  // Clear all localStorage items except 'darkMode' and 'i18nextLng'
  Object.keys(localStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  // Clear sessionStorage except for keys we want to keep
  const sessionKeysToKeep = ["i18nextLng"];
  Object.keys(sessionStorage).forEach((key) => {
    if (!sessionKeysToKeep.includes(key)) {
      sessionStorage.removeItem(key);
    }
  });

  // Reset all stores after clearing persist storage
  usePaginationStore.getState().resetPaginationState();
  useAiImportStore.getState().reset();
  useUserStore.getState().reset();
  useDocumentChatStore.getState().clearAllConversations();

  // Forces the store to immediately load any saved data from sessionStorage
  // Since we are clearing the sessionStorage, null is returned and the store is reset to its initial state
  await usePaginationStore.persist.rehydrate();

  // Clear Apollo client cache
  client.clearStore();
}
